import type { HoComponents } from '@pos/api-client';
import { hoApi } from '../api';

export type CustomerAccountItem = HoComponents['schemas']['CustomerAccountListItem'];
export type CustomerAccountDetail = HoComponents['schemas']['CustomerAccountDetail'];
export type CustomerAccountEvent = HoComponents['schemas']['CustomerAccountEventDto'];

/** The server checks these again (ADR 0082). */
export const ACCOUNTS_READ = 'ho.customer_accounts.read';
export const ACCOUNTS_REVEAL = 'ho.customer_accounts.reveal';
export const ACCOUNTS_MANAGE = 'ho.customer_accounts.manage';
export const ACCOUNTS_ERASE = 'ho.customer_accounts.erase';

export const ACCOUNT_STATUSES = ['active', 'locked', 'erased'] as const;
export type AccountStatus = (typeof ACCOUNT_STATUSES)[number];

export const STATUS_TAG = { active: 'active', locked: 'failed', erased: 'inactive' } as const satisfies Record<
  AccountStatus,
  string
>;

export const COMMANDS = ['lock', 'unlock', 'sign_out', 'clear_lockout', 'erase'] as const;
export type AccountCommand = (typeof COMMANDS)[number];

export const accountsKey = (search: string, status: string, page: number, pageSize: number) =>
  ['ho', 'customer-accounts', search, status, page, pageSize] as const;
export const accountKey = (id: string) => ['ho', 'customer-account', id] as const;
export const eventsKey = (id: string) => ['ho', 'customer-account', id, 'events'] as const;

export async function listAccounts(search: string, status: string, page: number, pageSize: number) {
  const { data, error } = await hoApi.GET('/api/v1/onlineordering/customer-accounts', {
    params: { query: { search: search || undefined, status: status || undefined, page, pageSize } },
  });
  if (!data) throw error;
  return data;
}

export async function getAccount(id: string): Promise<CustomerAccountDetail> {
  const { data, error } = await hoApi.GET('/api/v1/onlineordering/customer-accounts/{id}', {
    params: { path: { id } },
  });
  if (!data) throw error;
  return data;
}

export async function listEvents(id: string, before?: string) {
  const { data, error } = await hoApi.GET('/api/v1/onlineordering/customer-accounts/{id}/events', {
    params: { path: { id }, query: { before } },
  });
  if (!data) throw error;
  return data;
}

/**
 * The whole IP and user agent of a page of events (as `listEvents` with the same `before`), for a security audit (ADR 0123). Needs the
 * reveal permission; every reveal is audited.
 */
export async function revealNetwork(id: string, before?: string) {
  const { data, error } = await hoApi.POST('/api/v1/onlineordering/customer-accounts/{id}/events/network', {
    params: { path: { id }, query: { before } },
  });
  if (!data) throw error;
  return data;
}

export async function revealContact(id: string) {
  const { data, error } = await hoApi.POST('/api/v1/onlineordering/customer-accounts/{id}/reveal', {
    params: { path: { id } },
  });
  if (!data) throw error;
  return data;
}

export async function sendCommand(id: string, command: AccountCommand): Promise<void> {
  const { response, error } = await hoApi.POST('/api/v1/onlineordering/customer-accounts/{id}/commands', {
    params: { path: { id } },
    body: { command },
  });
  if (!response.ok) throw error ?? { status: response.status };
}
